import React from "react";
import { useTranslation } from "react-i18next";
import type { TransformedProduct } from "../../../../types/productApiTypes";
import WishlistToggleButton from "../../../../../wishlist/components/common/WishlistToggleButton";
import CompareToggleButton from "../../../../../compare/components/common/CompareToggleButton";

import ProductHeader from "./ProductHeader";
import ProductPriceDetails from "./ProductPriceDetails";
import ProductShortDescription from "./ProductShortDescription";
import ProductAttributes from "./ProductAttributes";
import ProductActions from "./ProductActions";
import ProductMeta from "./ProductMeta";
import GroupedProductContent from "./GroupedProduct/GroupedProductContent";
import DeliveryEstimateDisplay from "../../DeliveryEstimateDisplay";
import BuyXPayYDisclaimer from "./BuyXPayYDisclaimer";

interface ProductInfoDetailsProps {
  product: TransformedProduct;
  selectedVariation: TransformedProduct | null;
  selectedAttributes: { [key: string]: string };
  onAttributeSelect: (attributeTaxonomy: string, termSlug: string) => void;
  showDetailsLink?: boolean;
}

const ProductInfoDetails: React.FC<ProductInfoDetailsProps> = ({
  product,
  selectedVariation,
  selectedAttributes,
  onAttributeSelect,
  showDetailsLink,
}) => {
  const { ready: areTranslationsReady } = useTranslation();

  if (!areTranslationsReady) {
    return null;
  }

  // If the product is of type 'grouped', render the specialized component.
  if (product.type === "grouped") {
    return <GroupedProductContent product={product} />;
  }

  // --- ID LOGIC ---

  // 1. Wishlist: Always use the Parent Product ID (product.id).
  // This ensures the main product is saved, regardless of current selection.
  const wishlistTargetId = product.id;

  // 2. Compare: Use Selected Variation ID if available, otherwise Parent ID.
  // This allows comparing specific configurations (e.g. "Red Small" vs "Blue Medium").
  const compareTargetId = selectedVariation ? selectedVariation.id : product.id;

  // Otherwise, render the standard details for external/simple/variable/variation type products.
  return (
    <div className="product-info-details">
      <div className="product-info-details__top-actions">
        <WishlistToggleButton
          productId={wishlistTargetId}
          className="product-info-details__wishlist-toggle"
        />
        <CompareToggleButton
          productId={compareTargetId}
          className="product-info-details__compare-toggle"
        />
      </div>

      <ProductHeader product={product} />

      <ProductPriceDetails
        product={product}
        selectedVariation={selectedVariation}
      />

      <ProductShortDescription
        product={product}
        selectedVariation={selectedVariation}
      />

      <ProductAttributes
        product={product}
        selectedAttributes={selectedAttributes}
        onAttributeSelect={onAttributeSelect}
      />

      <ProductActions
        product={product}
        selectedVariation={selectedVariation}
        selectedAttributes={selectedAttributes}
        showDetailsLink={showDetailsLink}
      />

      <BuyXPayYDisclaimer promotion={product.buyXPayYPromotion} />

      {/* Only show delivery estimate if the product is NOT virtual */}
      {!product.virtual && (
        <DeliveryEstimateDisplay estimate={product.deliveryEstimation} />
      )}

      <ProductMeta product={product} selectedVariation={selectedVariation} />
    </div>
  );
};

export default ProductInfoDetails;
